/** True when a token carries at least one letter/digit — used to drop stray punctuation-only
    tokens (e.g. a lone '-' left behind between two real words) that match nothing and mean
    nothing on their own. */
function hasAlnum(token: string): boolean {
  return /[a-z0-9]/i.test(token);
}

/**
 * Strips diacritics (NFD decomposition + combining-mark removal) and collapses whitespace/
 * punctuation runs to single spaces, WITHOUT case-folding. Preserves hyphens, slashes,
 * underscores and parentheses — the characters that appear inside a real license number
 * ('MH-12/GC/0000/0000', 'LN_432156', 'P/HQ/MH/15/1367(P6685)') — so one never gets torn into
 * multiple meaningless tokens. This is the SINGLE splitting pass every other function in this
 * module derives from (by lowercasing its output), so the cased and case-folded token streams
 * can never drift out of alignment with each other.
 */
function normalizeSearchTextPreservingCase(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9/_()-]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

/**
 * Case-folded, diacritic-stripped, punctuation-collapsed normalization — see
 * {@link normalizeSearchTextPreservingCase}. The single normalization pass every vocabulary
 * entry AND every typed query goes through, so a match compares like-for-like.
 */
export function normalizeSearchText(s: string): string {
  return normalizeSearchTextPreservingCase(s).toLowerCase();
}

/** Normalizes WITHOUT case-folding, then splits on whitespace, dropping any punctuation-only
    token. Index-aligned with {@link tokenizeSearchQuery} (both split the same
    {@link normalizeSearchTextPreservingCase} pass) — lets a caller recover the original casing
    behind a matched/unmatched token, e.g. for a licenseNo candidate value the user actually
    typed. Empty/whitespace-only input yields []. */
export function tokenizeSearchQueryPreservingCase(s: string): string[] {
  const normalized = normalizeSearchTextPreservingCase(s);
  return normalized.length === 0 ? [] : normalized.split(' ').filter(hasAlnum);
}

/** Case-folded tokens, derived from {@link tokenizeSearchQueryPreservingCase} so the two can
    never disagree on where a token boundary falls. Empty/whitespace-only input yields []. */
export function tokenizeSearchQuery(s: string): string[] {
  return tokenizeSearchQueryPreservingCase(s).map((t) => t.toLowerCase());
}
